import {Component, OnDestroy, OnInit, inject, output, input, effect} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {DataService} from "../../../../services/data.service";
import {ProjectStatus, ViewMode, Right, TeamRole} from "../../../interfaces";
import {AccessService} from "../../../../services/access.service";
import moment from "moment/moment";
import {Subscription} from "rxjs";
import {ToastrService} from "ngx-toastr";
import {FormService} from "../../../../services/form.service";
import {NgIf, NgFor, DatePipe} from '@angular/common';
import {TooltipModule} from 'ngx-bootstrap/tooltip';

@Component({
    selector: 'app-research-reporting',
    templateUrl: './research-reporting.component.html',
    styleUrl: './research-reporting.component.scss',
    imports: [NgIf, TooltipModule, FormsModule, ReactiveFormsModule, NgFor, DatePipe]
})
export class ResearchReportingComponent implements OnInit, OnDestroy {

    accessService = inject(AccessService);
    private dataService = inject(DataService);
    private fb = inject(UntypedFormBuilder);
    private toastrService = inject(ToastrService);
    private formService = inject(FormService);

    readonly PID = input.required<any>();
    readonly reloadTrigger = input<number>(0);

    readonly changeProject = output();
    readonly changeViewMode = output<boolean>();

    readonly ViewMode = ViewMode;
    readonly ProjectStatus = ProjectStatus;
    readonly Right = Right;
    readonly TeamRole = TeamRole;

    viewMode!: ViewMode;
    statusReports: any[] = [];
    form!: UntypedFormGroup;
    currentReport: any = null;
    project: any;

    reportsLoaded = false;
    projectLoaded = false;

    placeHolderContent = '';
    placeHolderTime = '';
    placeHolderCosts = '';

    private contentChanges$: Subscription;
    private timeChanges$: Subscription;
    private costsChanges$: Subscription;

    statusMissing = false;

    private reloadEffect = effect(() => {
        if (this.reloadTrigger() > 0) {
            this.getStatusReports();
        }
    })

    ngOnInit(): void {
        this.viewMode = this.PID() == '0' ? ViewMode.INSERT : ViewMode.VIEW;

        this.form = this.fb.group({
            id: [null],
            id_project: [this.PID()],
            id_user: [this.accessService.user.id],
            description: [null],

            content: [null, Validators.required],
            time: [null, Validators.required],
            costs: [null, Validators.required],

            action_content: [null],
            action_time: [null],
            action_costs: [null],
        });

        if (this.PID() !== '0') {
            this.getStatusReports();
        }

        this.contentChanges$ = this.form.get('content').valueChanges.subscribe(val => {
            if (val == 'success') {
                this.placeHolderContent = '';
            } else if (val == 'warning') {
                this.placeHolderContent = 'Abweichung vorhanden, aber unkritisch';
            } else {
                this.placeHolderContent = 'Kritisch, sofortige Maßnahme/Entscheidung erforderlich';
            }

            if (val == 'success') {
                this.formService.removeValidators(this.form, ['action_content'], Validators.required)
            } else {
                this.formService.addValidators(this.form, ['action_content'], Validators.required)
            }
        });

        this.timeChanges$ = this.form.get('time').valueChanges.subscribe(val => {
            if (val == 'success') {
                this.placeHolderTime = '';
            } else if (val == 'warning') {
                this.placeHolderTime = 'Abweichung vorhanden, aber unkritisch';
            } else {
                this.placeHolderTime = 'Kritisch, sofortige Maßnahme/Entscheidung erforderlich';
            }

            if (val == 'success') {
                this.formService.removeValidators(this.form, ['action_time'], Validators.required)
            } else {
                this.formService.addValidators(this.form, ['action_time'], Validators.required)
            }
        });

        this.costsChanges$ = this.form.get('costs').valueChanges.subscribe(val => {
            if (val == 'success') {
                this.placeHolderCosts = '';
            } else if (val == 'warning') {
                this.placeHolderCosts = 'Abweichung vorhanden, aber unkritisch';
            } else {
                this.placeHolderCosts = 'Kritisch, sofortige Maßnahme/Entscheidung erforderlich';
            }

            if (val == 'success') {
                this.formService.removeValidators(this.form, ['action_costs'], Validators.required)
            } else {
                this.formService.addValidators(this.form, ['action_costs'], Validators.required)
            }
        });
    }

    ngOnDestroy() {
        this.contentChanges$?.unsubscribe();
        this.timeChanges$?.unsubscribe();
        this.costsChanges$?.unsubscribe();
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
        if (this.viewMode == ViewMode.EDIT || this.viewMode == ViewMode.INSERT) {
            this.changeViewMode.emit(true);
        } else {
            this.changeViewMode.emit(false);
        }
    }

    getStatusReports(): void {
        this.reportsLoaded = false;
        this.dataService.request('ReportResearch/listAll', {
            PID: this.PID()
        }).subscribe(response => {
            this.statusReports = response.statusReports;

            this.statusReports.forEach(report => {
                // replace linebreaks with <br> tags
                report.descriptionWithBreaks = report.description ? report.description.replace(/\n/g, '<br>') : '';
                report.actionWithBreaks = report.action_content ? report.action_content.replace(/\n/g, '<br>') : '';
                report.timeWithBreaks = report.action_time ? report.action_time.replace(/\n/g, '<br>') : '';
                report.costsWithBreaks = report.action_costs ? report.action_costs.replace(/\n/g, '<br>') : '';
            });

            this.reportsLoaded = true;
            if (this.reportsLoaded && this.projectLoaded) this.highlightMissing();
        });

        this.projectLoaded = false;
        this.dataService.request('Research/get', {
            PID: this.PID()
        }).subscribe(response => {
            this.project = response.project;
            this.projectLoaded = true;

            if (this.reportsLoaded && this.projectLoaded) this.highlightMissing();
        });
    }

    highlightMissing() {
        // highlight missing status
        if (this.project.status == ProjectStatus.IN_PROGRESS) {

            if (this.statusReports.length === 0) {
                this.statusMissing = true;
            } else {
                const lastStatusReport = this.statusReports[0];

                // if the month of the last status report is before the current month, the status is missing
                this.statusMissing = moment(lastStatusReport.created_at).isBefore(moment(), 'month');
            }
        } else if (this.project.status == ProjectStatus.RELEASED) {
            if (this.statusReports.length === 0) {
                this.statusMissing = true;
            }
        } else {
            this.statusMissing = false;
        }
    }

    patchValue(control: string, value: string): void {
        this.form.get(control).patchValue(value);
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.currentReport ? this.currentReport.id : null,
            id_project: this.currentReport ? this.currentReport.id_project : this.PID(),
            id_user: this.currentReport ? this.currentReport.id_user : this.accessService.user.id,
            description: this.currentReport ? this.currentReport.description : null,

            content: this.currentReport ? this.currentReport.content : null,
            time: this.currentReport ? this.currentReport.time : null,
            costs: this.currentReport ? this.currentReport.costs : null,

            action_content: this.currentReport ? this.currentReport.action_content : null,
            action_time: this.currentReport ? this.currentReport.action_time : null,
            action_costs: this.currentReport ? this.currentReport.action_costs : null,
        });
    }

    saveStatusReport(): void {
        this.dataService.request('ReportResearch/save', {
            status_report: this.form.getRawValue(),
        }).subscribe(() => {
            this.toastrService.success('Erfolgreich gespeichert')
            this.currentReport = null;
            this.setMode(ViewMode.VIEW);
            this.getStatusReports();

            this.changeProject.emit();
        });
    }

    updateStatusReport(): void {
        this.dataService.request('ReportResearch/update', {
            status_report: this.form.getRawValue(),
        }).subscribe(() => {
            this.toastrService.success('Erfolgreich gespeichert')
            this.currentReport = null;
            this.setMode(ViewMode.VIEW);
            this.getStatusReports();
        });
    }

    cancelForm(): void {
        this.currentReport = null;
        this.setMode(ViewMode.VIEW);
        this.getStatusReports();
    }

    addStatusReport(): void {
        this.currentReport = null;
        this.setMode(ViewMode.INSERT);
        this.patchForm();
    }

    editStatusReport(entry: any): void {
        this.currentReport = entry;
        this.setMode(ViewMode.EDIT);
        this.patchForm();
    }

    saveFeedback(status_report: any) {
        this.dataService.request('ReportResearch/saveFeedback', {
            status_report: status_report,
        }).subscribe(() => {
            this.toastrService.success('Feedback gespeichert')
        });
    }
}
